'use client'

import { useState, useMemo } from 'react'
import Link from 'next/link'
import { usePortfolio } from '@/contexts/PortfolioContext'
import { useAuth } from '@/contexts/AuthContext'
import { TierGate } from '@/components/app'

// =============================================================================
// HELPERS
// =============================================================================

function formatCurrency(value: number, compact = false): string {
  if (compact) {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`
    return `$${value.toFixed(0)}`
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
}

// =============================================================================
// DATA
// =============================================================================

// Simulated historical data for performance chart
const performanceData = [
  { month: 'Sep', value: 48500000 },
  { month: 'Oct', value: 49200000 },
  { month: 'Nov', value: 51800000 },
  { month: 'Dec', value: 53100000 },
  { month: 'Jan', value: 54700000 },
  { month: 'Feb', value: 58400000 },
]

// Medium allocation data
const mediumAllocation = [
  { medium: 'Paintings', value: 42400000, share: 72.5, change: 12.4 },
  { medium: 'Sculpture', value: 8500000, share: 14.5, change: 6.2 },
  { medium: 'Prints', value: 4200000, share: 7.2, change: 18.8 },
  { medium: 'Photography', value: 2100000, share: 3.6, change: -2.4 },
  { medium: 'Drawings', value: 1200000, share: 2.2, change: 4.1 },
]

// Era allocation data
const eraAllocation = [
  { era: 'Contemporary', value: 35200000, share: 60.2, change: 14.2 },
  { era: 'Post-War', value: 15800000, share: 27.0, change: 8.4 },
  { era: 'Modern', value: 5400000, share: 9.2, change: 5.1 },
  { era: '19th Century', value: 2000000, share: 3.6, change: 2.8 },
]

// Geographic exposure
const geographicExposure = [
  { region: 'Americas', value: 32400000, share: 55.4, artists: 8 },
  { region: 'Europe', value: 18200000, share: 31.1, artists: 5 },
  { region: 'Asia-Pacific', value: 6800000, share: 11.6, artists: 3 },
  { region: 'Africa', value: 1000000, share: 1.9, artists: 1 },
]

// Risk metrics
const riskMetrics = {
  concentration: {
    topHolding: 72.5, // % of portfolio in top holding
    top3: 89.2,
    hhi: 0.54, // Herfindahl-Hirschman Index
    rating: 'High',
  },
  liquidity: {
    highLiquidity: 45.2, // % easily saleable
    mediumLiquidity: 38.4,
    lowLiquidity: 16.4,
    rating: 'Medium',
  },
  volatility: {
    beta: 1.24, // vs market
    stdDev: 18.2,
    maxDrawdown: -12.4,
    rating: 'Above Average',
  },
}

// Upcoming auctions for portfolio artists
const upcomingAuctions = [
  { artist: 'Jean-Michel Basquiat', title: 'Untitled (Skull)', house: "Christie's", date: 'Mar 12', estimate: '$15-20M' },
  { artist: 'Yayoi Kusama', title: 'Infinity Nets', house: "Sotheby's", date: 'Mar 18', estimate: '$3-4M' },
  { artist: 'Banksy', title: 'Girl with Balloon', house: 'Phillips', date: 'Mar 22', estimate: '$800K-1.2M' },
]

// Comparable recent sales
const comparableSales = [
  { artist: 'Jean-Michel Basquiat', work: 'Untitled (Devil)', yourValue: '$42.4M', salePrice: '$48.2M', premium: '+13.7%', date: '2 weeks ago' },
  { artist: 'Yayoi Kusama', work: 'Pumpkin (similar)', yourValue: '$8.9M', salePrice: '$9.8M', premium: '+10.1%', date: '1 month ago' },
  { artist: 'Gerhard Richter', work: 'Abstraktes Bild (similar)', yourValue: '$18.2M', salePrice: '$16.4M', premium: '-9.9%', date: '3 weeks ago' },
]

// AI recommendations
const aiRecommendations = [
  { type: 'diversify', title: 'Reduce Basquiat concentration', description: 'Consider selling 20% of Basquiat position to reduce single-artist risk', priority: 'high' },
  { type: 'opportunity', title: 'Add Asian Contemporary exposure', description: 'Portfolio underweight in fastest-growing segment. Consider Yoshitomo Nara, Lee Ufan.', priority: 'medium' },
  { type: 'alert', title: 'Richter position underperforming', description: 'Recent comparables trading below your cost basis. Monitor closely.', priority: 'medium' },
]

// =============================================================================
// COMPONENTS
// =============================================================================

function PerformanceChart({ data }: { data: { month: string; value: number }[] }) {
  const maxValue = Math.max(...data.map(d => d.value))
  const minValue = Math.min(...data.map(d => d.value))
  const range = maxValue - minValue || 1
  const height = 160
  const width = 100

  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * width
    const y = height - ((d.value - minValue) / range) * height * 0.8 - height * 0.1
    return { x, y, ...d }
  })

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
  const areaD = `${pathD} L ${width} ${height} L 0 ${height} Z`

  return (
    <div className="relative h-[160px] w-full">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="var(--positive)" stopOpacity="0.3" />
            <stop offset="100%" stopColor="var(--positive)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaD} fill="url(#chartGradient)" />
        <path d={pathD} fill="none" stroke="var(--positive)" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-text-tertiary px-1">
        {data.map((d, i) => (
          <span key={i}>{d.month}</span>
        ))}
      </div>
    </div>
  )
}

function Sparkline({ positive }: { positive: boolean }) {
  const points = positive
    ? "0,20 10,18 20,15 30,16 40,12 50,8 60,10 70,6 80,4"
    : "0,8 10,10 20,12 30,10 40,14 50,16 60,14 70,18 80,20"

  return (
    <svg width="60" height="20" className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke={positive ? 'var(--positive)' : 'var(--negative)'}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function AllocationBar({ data }: { data: { name: string; share: number; color: string }[] }) {
  return (
    <div className="flex rounded-full h-3 overflow-hidden">
      {data.map((item, i) => (
        <div
          key={item.name}
          className="h-full"
          style={{ width: `${item.share}%`, background: item.color }}
          title={`${item.name}: ${item.share}%`}
        />
      ))}
    </div>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function PortfolioPage() {
  const { items, removeItem, totalValue, totalCost, totalGain, totalGainPercent } = usePortfolio()
  const { user } = useAuth()
  const [sortBy, setSortBy] = useState<'value' | 'gain' | 'date'>('value')
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list')

  const isFreeUser = user?.tier === 'free' || !user

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      switch (sortBy) {
        case 'value':
          return b.currentPrice - a.currentPrice
        case 'gain':
          return (b.currentPrice - b.addedPrice) - (a.currentPrice - a.addedPrice)
        case 'date':
          return new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime()
        default:
          return 0
      }
    })
  }, [items, sortBy])

  const handleExport = () => {
    alert('Export functionality coming soon. This will generate a PDF valuation report.')
  }

  // Empty state
  if (items.length === 0) {
    return (
      <div className="px-4 md:px-6 py-6 max-w-[1400px] mx-auto">
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="w-16 h-16 rounded-2xl bg-accent-primary/10 flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h2 className="font-display text-2xl font-light mb-2">Your Portfolio is Empty</h2>
          <p className="text-text-secondary text-sm mb-6 max-w-md">
            Add works from lot pages or artist profiles to track their performance, get AI insights, and manage your collection.
          </p>
          <div className="flex gap-3">
            <Link
              href="/app/market"
              className="pill px-4 py-2 text-sm"
              style={{ background: 'var(--accent-primary)', color: 'white' }}
            >
              Explore Market
            </Link>
            <Link
              href="/app/artists"
              className="pill px-4 py-2 text-sm"
              style={{ background: 'transparent', border: '1px solid var(--border-standard)', color: 'var(--text-primary)' }}
            >
              Browse Artists
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 md:px-6 py-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="font-display text-2xl md:text-3xl font-light">Portfolio</h1>
            <span className="text-xs px-2 py-1 rounded-full bg-positive/10 text-positive">
              +{totalGainPercent.toFixed(1)}% all time
            </span>
          </div>
          <p className="text-text-secondary text-sm">
            Track, analyze, and optimize your collection
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TierGate feature="export" showPrompt={false}>
            <button
              onClick={handleExport}
              className="pill px-4 py-2 text-sm flex items-center gap-2"
              style={{ background: 'transparent', border: '1px solid var(--border-standard)', color: 'var(--text-primary)' }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export PDF
            </button>
          </TierGate>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4">
          <p className="text-xs text-text-tertiary mb-1">Total Value</p>
          <p className="text-2xl font-display font-light">{formatCurrency(totalValue, true)}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-text-tertiary mb-1">Total Cost</p>
          <p className="text-2xl font-display font-light">{formatCurrency(totalCost, true)}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-text-tertiary mb-1">Unrealized Gain</p>
          <p className={`text-2xl font-display font-light ${totalGain >= 0 ? 'text-positive' : 'text-negative'}`}>
            {totalGain >= 0 ? '+' : ''}{formatCurrency(totalGain, true)}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-text-tertiary mb-1">Return</p>
          <p className={`text-2xl font-display font-light ${totalGainPercent >= 0 ? 'text-positive' : 'text-negative'}`}>
            {totalGainPercent >= 0 ? '+' : ''}{totalGainPercent.toFixed(1)}%
          </p>
        </div>
      </div>

      {/* Performance Chart + AI Recommendations */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        {/* Performance Chart */}
        <div className="lg:col-span-2 card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-medium">Portfolio Performance</h2>
              <p className="text-xs text-text-tertiary">Last 6 months</p>
            </div>
            <div className="text-right">
              <p className="text-xl font-display font-light text-positive">
                +{((performanceData[performanceData.length - 1].value - performanceData[0].value) / performanceData[0].value * 100).toFixed(1)}%
              </p>
              <p className="text-xs text-text-tertiary">6-month return</p>
            </div>
          </div>
          <PerformanceChart data={performanceData} />
        </div>

        {/* AI Recommendations */}
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--accent-primary)', color: 'white' }}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            </div>
            <h2 className="font-medium">AI Recommendations</h2>
          </div>
          <div className="space-y-3">
            {aiRecommendations.map((rec, i) => (
              <div
                key={i}
                className={`p-3 rounded-lg border ${
                  rec.priority === 'high'
                    ? 'border-negative/30 bg-negative/5'
                    : 'border-border bg-page'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-medium ${
                    rec.type === 'diversify' ? 'text-accent-warm' :
                    rec.type === 'opportunity' ? 'text-positive' :
                    'text-negative'
                  }`}>
                    {rec.type === 'diversify' ? '⚖️' : rec.type === 'opportunity' ? '💡' : '⚠️'}
                  </span>
                  <span className="text-sm font-medium">{rec.title}</span>
                </div>
                <p className="text-xs text-text-tertiary">{rec.description}</p>
              </div>
            ))}
          </div>
          {isFreeUser && (
            <Link
              href="/get-started"
              className="block mt-4 text-center text-sm text-accent-primary hover:underline"
            >
              Get personalized recommendations →
            </Link>
          )}
        </div>
      </div>

      {/* Allocation Breakdown */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        {/* By Medium */}
        <div className="card p-5">
          <h2 className="font-medium mb-4">By Medium</h2>
          <div className="space-y-3">
            {mediumAllocation.map((item) => (
              <div key={item.medium} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm" style={{ background: 'var(--accent-primary)', opacity: 1 - (mediumAllocation.indexOf(item) * 0.15) }} />
                  <span className="text-sm">{item.medium}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm tabular-nums">{item.share}%</span>
                  <span className={`text-xs tabular-nums ${item.change >= 0 ? 'text-positive' : 'text-negative'}`}>
                    {item.change >= 0 ? '+' : ''}{item.change}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* By Era */}
        <div className="card p-5">
          <h2 className="font-medium mb-4">By Era</h2>
          <div className="space-y-3">
            {eraAllocation.map((item) => (
              <div key={item.era} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm" style={{ background: 'var(--accent-secondary)', opacity: 1 - (eraAllocation.indexOf(item) * 0.2) }} />
                  <span className="text-sm">{item.era}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm tabular-nums">{item.share}%</span>
                  <span className={`text-xs tabular-nums ${item.change >= 0 ? 'text-positive' : 'text-negative'}`}>
                    {item.change >= 0 ? '+' : ''}{item.change}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Geographic Exposure */}
        <div className="card p-5">
          <h2 className="font-medium mb-4">Geographic Exposure</h2>
          <div className="space-y-3">
            {geographicExposure.map((item) => (
              <div key={item.region} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm" style={{ background: 'var(--accent-warm)', opacity: 1 - (geographicExposure.indexOf(item) * 0.2) }} />
                  <span className="text-sm">{item.region}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm tabular-nums">{item.share}%</span>
                  <span className="text-xs text-text-tertiary">{item.artists} artists</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Risk Metrics */}
      <div className="card p-6 mb-6">
        <h2 className="font-medium mb-4">Risk Analysis</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {/* Concentration Risk */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-text-secondary">Concentration</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                riskMetrics.concentration.rating === 'High'
                  ? 'bg-negative/10 text-negative'
                  : riskMetrics.concentration.rating === 'Medium'
                  ? 'bg-accent-warm/10 text-accent-warm'
                  : 'bg-positive/10 text-positive'
              }`}>
                {riskMetrics.concentration.rating}
              </span>
            </div>
            <div className="space-y-1 text-xs text-text-tertiary">
              <div className="flex justify-between">
                <span>Top holding</span>
                <span className="tabular-nums">{riskMetrics.concentration.topHolding}%</span>
              </div>
              <div className="flex justify-between">
                <span>Top 3 holdings</span>
                <span className="tabular-nums">{riskMetrics.concentration.top3}%</span>
              </div>
            </div>
          </div>

          {/* Liquidity */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-text-secondary">Liquidity</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                riskMetrics.liquidity.rating === 'High'
                  ? 'bg-positive/10 text-positive'
                  : riskMetrics.liquidity.rating === 'Medium'
                  ? 'bg-accent-warm/10 text-accent-warm'
                  : 'bg-negative/10 text-negative'
              }`}>
                {riskMetrics.liquidity.rating}
              </span>
            </div>
            <div className="space-y-1 text-xs text-text-tertiary">
              <div className="flex justify-between">
                <span>High liquidity</span>
                <span className="tabular-nums">{riskMetrics.liquidity.highLiquidity}%</span>
              </div>
              <div className="flex justify-between">
                <span>Medium liquidity</span>
                <span className="tabular-nums">{riskMetrics.liquidity.mediumLiquidity}%</span>
              </div>
            </div>
          </div>

          {/* Volatility */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-text-secondary">Volatility</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-accent-warm/10 text-accent-warm">
                {riskMetrics.volatility.rating}
              </span>
            </div>
            <div className="space-y-1 text-xs text-text-tertiary">
              <div className="flex justify-between">
                <span>Beta vs market</span>
                <span className="tabular-nums">{riskMetrics.volatility.beta}x</span>
              </div>
              <div className="flex justify-between">
                <span>Max drawdown</span>
                <span className="tabular-nums text-negative">{riskMetrics.volatility.maxDrawdown}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Auctions + Comparable Sales */}
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Upcoming Auctions */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium">Upcoming Auctions</h2>
            <span className="text-xs text-text-tertiary">Your artists</span>
          </div>
          <div className="space-y-3">
            {upcomingAuctions.map((auction, i) => (
              <div key={i} className="py-2 border-b border-border-subtle last:border-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">{auction.artist}</span>
                  <span className="text-xs text-text-tertiary">{auction.date}</span>
                </div>
                <p className="text-xs text-text-tertiary">{auction.title}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-accent-primary">{auction.house}</span>
                  <span className="text-xs tabular-nums">{auction.estimate}</span>
                </div>
              </div>
            ))}
          </div>
          <button className="w-full mt-4 py-2 rounded-lg border border-border text-text-secondary hover:border-accent-primary hover:text-accent-primary transition-colors text-sm flex items-center justify-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            Set auction alerts
          </button>
        </div>

        {/* Comparable Sales */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium">Comparable Sales</h2>
            <span className="text-xs text-text-tertiary">Recent comps</span>
          </div>
          <div className="space-y-3">
            {comparableSales.map((sale, i) => (
              <div key={i} className="py-2 border-b border-border-subtle last:border-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">{sale.artist}</span>
                  <span className={`text-xs font-medium tabular-nums ${
                    sale.premium.startsWith('+') ? 'text-positive' : 'text-negative'
                  }`}>
                    {sale.premium}
                  </span>
                </div>
                <p className="text-xs text-text-tertiary truncate">{sale.work}</p>
                <div className="flex items-center justify-between mt-1 text-xs">
                  <span className="text-text-tertiary">Your value: {sale.yourValue}</span>
                  <span className="tabular-nums">Sold: {sale.salePrice}</span>
                </div>
              </div>
            ))}
          </div>
          <TierGate feature="export" showPrompt={false}>
            <button className="w-full mt-4 py-2 rounded-lg border border-border text-text-secondary hover:border-accent-primary hover:text-accent-primary transition-colors text-sm flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Generate valuation report
            </button>
          </TierGate>
        </div>
      </div>

      {/* Holdings */}
      <div className="card overflow-hidden mb-6">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="font-medium">Holdings ({items.length})</h2>
          <div className="flex items-center gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'value' | 'gain' | 'date')}
              className="text-sm bg-transparent border border-border rounded-lg px-3 py-1.5 text-text-secondary focus:outline-none focus:border-border-hover"
            >
              <option value="value">Sort by Value</option>
              <option value="gain">Sort by Gain</option>
              <option value="date">Sort by Date Added</option>
            </select>
            <div className="flex border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 ${viewMode === 'list' ? 'bg-page' : ''}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 ${viewMode === 'grid' ? 'bg-page' : ''}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {viewMode === 'list' ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-subtle">
                  <th className="text-left px-4 py-3 text-xs font-medium text-text-tertiary">Work</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-text-tertiary hidden md:table-cell">Artist</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-text-tertiary">Cost Basis</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-text-tertiary">Current Value</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-text-tertiary">Gain/Loss</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-text-tertiary hidden sm:table-cell">Trend</th>
                  <th className="px-4 py-3 text-xs font-medium text-text-tertiary w-10"></th>
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((item) => {
                  const gain = item.currentPrice - item.addedPrice
                  const gainPercent = (gain / item.addedPrice) * 100
                  const isPositive = gain >= 0

                  return (
                    <tr key={item.lotId} className="border-b border-border-subtle last:border-0 hover:bg-page transition-colors">
                      <td className="px-4 py-4">
                        <Link href={`/app/lots/${item.lotId}`} className="hover:text-accent-primary transition-colors">
                          <p className="font-medium text-sm">{item.title}</p>
                          <p className="text-xs text-text-tertiary">{item.year} · {item.medium.split(',')[0]}</p>
                        </Link>
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        <Link href={`/app/artists/${item.artistSlug}`} className="text-sm text-text-secondary hover:text-accent-primary transition-colors">
                          {item.artist}
                        </Link>
                      </td>
                      <td className="px-4 py-4 text-right text-sm tabular-nums">{formatCurrency(item.addedPrice, true)}</td>
                      <td className="px-4 py-4 text-right text-sm font-medium tabular-nums">{formatCurrency(item.currentPrice, true)}</td>
                      <td className="px-4 py-4 text-right">
                        <span className={`text-sm font-medium tabular-nums ${isPositive ? 'text-positive' : 'text-negative'}`}>
                          {isPositive ? '+' : ''}{formatCurrency(gain, true)}
                        </span>
                        <span className={`block text-xs tabular-nums ${isPositive ? 'text-positive' : 'text-negative'}`}>
                          {isPositive ? '+' : ''}{gainPercent.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-4 hidden sm:table-cell">
                        <Sparkline positive={isPositive} />
                      </td>
                      <td className="px-4 py-4">
                        <button
                          onClick={() => removeItem(item.lotId)}
                          className="p-1.5 rounded-lg hover:bg-negative/10 text-text-tertiary hover:text-negative transition-colors"
                          title="Remove from portfolio"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
            {sortedItems.map((item) => {
              const gain = item.currentPrice - item.addedPrice
              const gainPercent = (gain / item.addedPrice) * 100
              const isPositive = gain >= 0

              return (
                <div key={item.lotId} className="border border-border rounded-xl p-4 hover:border-border-hover transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <Link href={`/app/lots/${item.lotId}`} className="flex-1">
                      <p className="font-medium text-sm hover:text-accent-primary transition-colors">{item.title}</p>
                      <p className="text-xs text-text-tertiary">{item.artist}</p>
                    </Link>
                    <button
                      onClick={() => removeItem(item.lotId)}
                      className="p-1 rounded hover:bg-negative/10 text-text-tertiary hover:text-negative transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-lg font-display font-light">{formatCurrency(item.currentPrice, true)}</p>
                      <p className="text-xs text-text-tertiary">Cost: {formatCurrency(item.addedPrice, true)}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-medium ${isPositive ? 'text-positive' : 'text-negative'}`}>
                        {isPositive ? '+' : ''}{gainPercent.toFixed(1)}%
                      </p>
                      <Sparkline positive={isPositive} />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Upgrade CTA */}
      {isFreeUser && (
        <section className="card p-6 bg-gradient-to-br from-accent-primary/5 to-accent-secondary/5 border-accent-primary/20">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1">
              <h3 className="font-display text-xl font-light mb-2">Portfolio Intelligence</h3>
              <p className="text-sm text-text-secondary mb-4">
                Get AI-powered portfolio analysis, rebalancing recommendations, and comprehensive valuation reports for your collection.
              </p>
              <ul className="space-y-1">
                <li className="text-xs text-text-tertiary flex items-center gap-2">
                  <svg className="w-4 h-4 text-positive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  AI rebalancing recommendations
                </li>
                <li className="text-xs text-text-tertiary flex items-center gap-2">
                  <svg className="w-4 h-4 text-positive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  PDF valuation reports
                </li>
                <li className="text-xs text-text-tertiary flex items-center gap-2">
                  <svg className="w-4 h-4 text-positive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Risk analysis & alerts
                </li>
              </ul>
            </div>
            <div className="flex flex-col gap-2">
              <Link
                href="/get-started"
                className="px-6 py-3 rounded-xl font-medium text-white text-center"
                style={{ background: 'var(--accent-primary)' }}
              >
                From $199/mo
              </Link>
              <span className="text-xs text-text-tertiary text-center">Cancel anytime</span>
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
